import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

export default function AchcAccreditationPreparation() {
  useSEO({
    title: 'ACHC Accreditation Preparation Consulting | Advantixx',
    description: 'End-to-end ACHC accreditation preparation for home health agencies — gap analysis, policy development, staff training, mock surveys, and survey support. 100% pass rate.',
    keywords: 'ACHC accreditation preparation, home health accreditation consulting, ACHC gap analysis, accreditation roadmap',
    canonical: '/services/achc-accreditation-preparation',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-achc-prep';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'ACHC Accreditation Preparation Consulting',
      description: 'End-to-end ACHC accreditation preparation for home health agencies — gap analysis, policy development, staff training, mock surveys, and survey support.',
      provider: { '@type': 'Organization', name: 'Advantixx Home Health Accreditation Consultants' },
      serviceType: 'ACHC Accreditation Consulting',
      areaServed: { '@type': 'Country', name: 'United States' },
    });
    document.head.appendChild(script);
    return () => { document.getElementById('schema-achc-prep')?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-0">
        <div className="relative h-[400px] md:h-[480px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/c8f6f7030eb74017d94fb3dcadd30818.jpg"
            alt="ACHC Accreditation Preparation Consulting Service"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/50 to-[#0d0d0d]/20"></div>
          <div className="absolute bottom-0 left-0 right-0 max-w-5xl mx-auto px-6 md:px-10 pb-12">
            <span className="inline-block bg-[#d4af37] text-[#0d0d0d] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-4">Core Service</span>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight">
              ACHC Accreditation<br className="hidden md:block" />
              <span className="text-[#d4af37]"> Preparation Consulting</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="flex-1 min-w-0">
            <p className="text-[#bbb] text-lg leading-relaxed mb-8 border-l-4 border-[#d4af37] pl-6">
              Achieving ACHC accreditation is a complex, multi-phase process that requires expert guidance to navigate successfully. Advantixx provides comprehensive, end-to-end accreditation preparation — from your initial readiness assessment through your survey day and beyond.
            </p>

            <h2 className="text-2xl font-black text-white mb-6 mt-12"><span className="text-[#d4af37]">What&apos;s Included</span></h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
              {[
                { icon: 'ri-search-eye-line', title: 'Initial Gap Analysis', desc: 'Comprehensive assessment of your current operations against all applicable ACHC standards to identify deficiencies and build your accreditation roadmap.' },
                { icon: 'ri-file-list-3-line', title: 'Policy & Procedure Development', desc: 'Complete development or revision of your P&P manual to ensure full alignment with ACHC standards and CMS Conditions of Participation.' },
                { icon: 'ri-team-line', title: 'Staff Training Programs', desc: 'Customized training sessions for all staff levels — clinical, administrative, and management — covering ACHC standards and your agency\'s specific policies.' },
                { icon: 'ri-survey-line', title: 'Mock Survey', desc: 'A full simulation of the ACHC survey experience, including document review, staff interviews, and operational observation, with a detailed findings report.' },
                { icon: 'ri-calendar-check-line', title: 'Survey Day Support', desc: 'On-site or virtual support during your actual ACHC survey to ensure your team is prepared and confident throughout the process.' },
                { icon: 'ri-shield-check-line', title: 'Plan of Correction Assistance', desc: 'If deficiencies are cited, we help you develop a compliant, effective Plan of Correction that satisfies ACHC requirements.' },
              ].map((item, i) => (
                <div key={i} className="bg-[#141414] border border-[#d4af37]/15 rounded-xl p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                    <i className={`${item.icon} text-[#d4af37] text-lg`}></i>
                  </div>
                  <h3 className="text-white font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-[#777] text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-black text-white mb-6 mt-12"><span className="text-[#d4af37]">Our Process</span></h2>
            <div className="flex flex-col gap-5 mb-12">
              {[
                { phase: 'Phase 1', title: 'Discovery & Assessment', desc: 'We begin with a thorough review of your agency\'s current state — operations, documentation, staffing, and compliance posture — to establish a clear baseline.' },
                { phase: 'Phase 2', title: 'Roadmap Development', desc: 'Based on the gap analysis, we develop a detailed, prioritized accreditation roadmap with clear milestones, timelines, and responsibilities.' },
                { phase: 'Phase 3', title: 'Implementation Support', desc: 'We work alongside your team to implement all required changes — developing policies, training staff, and building your compliance infrastructure.' },
                { phase: 'Phase 4', title: 'Mock Survey & Final Prep', desc: 'A comprehensive mock survey identifies any remaining gaps and prepares your team for the actual ACHC survey experience.' },
                { phase: 'Phase 5', title: 'Survey & Post-Survey Support', desc: 'We support you through the actual survey and assist with any post-survey requirements, including Plans of Correction.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-5 bg-[#141414] border border-[#d4af37]/10 rounded-xl p-6">
                  <div className="flex-shrink-0">
                    <span className="inline-block bg-[#d4af37]/10 text-[#d4af37] text-xs font-bold px-3 py-1.5 rounded-md whitespace-nowrap">{item.phase}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm mb-2">{item.title}</h3>
                    <p className="text-[#777] text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-[#d4af37]/10 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-8 text-center">
              <h3 className="text-white font-black text-xl mb-3">Ready to Start Your ACHC Journey?</h3>
              <p className="text-[#888] text-sm mb-6 leading-relaxed">Book a free consultation with Dr. Villarreal and get a personalized accreditation roadmap for your agency.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">
                Book Free Consultation <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-28 flex flex-col gap-6">
              <div className="bg-[#141414] border border-[#d4af37]/15 rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">All Services</p>
                <ul className="flex flex-col gap-3">
                  {[
                    { label: 'Accreditation Preparation', path: '/services/achc-accreditation-preparation', active: true },
                    { label: 'Mock Survey Services', path: '/services/mock-survey' },
                    { label: 'Policy & Procedure Development', path: '/services/policy-procedure-development' },
                    { label: 'Compliance Audits', path: '/services/compliance-audit' },
                    { label: 'Staff Training', path: '/services/staff-training' },
                    { label: 'Ongoing Compliance Support', path: '/services/ongoing-compliance-support' },
                  ].map((item, i) => (
                    <li key={i}>
                      <Link to={item.path} className={`flex items-center gap-2 text-sm transition-colors duration-200 cursor-pointer ${item.active ? 'text-[#d4af37] font-semibold' : 'text-[#888] hover:text-[#d4af37]'}`}>
                        <i className={`ri-arrow-right-s-line ${item.active ? 'text-[#d4af37]' : 'text-[#555]'}`}></i>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#141414] border border-[#d4af37]/15 rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-3">Why Advantixx?</p>
                <ul className="flex flex-col gap-3">
                  {['ACHC Certified Consultant', 'Doctoral-level expertise', 'Nationwide service', '100% survey pass rate', 'Personalized approach'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#888]">
                      <i className="ri-checkbox-circle-fill text-[#d4af37] text-sm"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-[#d4af37]/15 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-6 text-center">
                <i className="ri-phone-line text-[#d4af37] text-3xl mb-3 block"></i>
                <p className="text-white font-bold text-sm mb-2">Speak with Dr. Villarreal</p>
                <p className="text-[#777] text-xs mb-4 leading-relaxed">Get expert answers to your accreditation questions today.</p>
                <Link to="/contact" className="block bg-[#d4af37] text-[#0d0d0d] font-bold text-xs px-5 py-2.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">Contact Us</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
